/**
 * Pure helpers for the /home/payu tab URL state.
 *
 * Tab state MUST live in the URL (?tab=payments) so:
 *   1. Refreshing keeps the current tab (not resetting to Connect)
 *   2. Deep links (e.g., PaymentReturn → "View payments") can target a tab
 *   3. Browser back/forward works across tab changes
 *
 * Extracted as pure functions so they can be unit-tested without a full
 * React render. The page component wires these to useSearchParams.
 */

export type PayuTab = 'connect' | 'payments' | 'links' | 'refunds';

export const PAYU_TABS: readonly PayuTab[] = [
  'connect',
  'payments',
  'links',
  'refunds',
] as const;

/**
 * Read the raw `tab` query param and normalise to a valid PayuTab value.
 * Unknown / missing → 'connect' (the default).
 */
export function resolveTabFromUrl(raw: string | null | undefined): PayuTab {
  if (!raw) return 'connect';
  return (PAYU_TABS as readonly string[]).includes(raw) ? (raw as PayuTab) : 'connect';
}

/**
 * Compute the next URLSearchParams when switching to a given tab.
 * For 'connect' (default), strip the param entirely so URLs stay clean.
 */
export function tabToSearchParams(
  prev: URLSearchParams,
  next: PayuTab
): URLSearchParams {
  const params = new URLSearchParams(prev);
  if (next === 'connect') {
    params.delete('tab');
  } else {
    params.set('tab', next);
  }
  return params;
}

/**
 * Build the full /home/payu URL path for a given tab, suitable for navigate()
 * or <a href>. Used by PaymentReturn ("View payments" → /home/payu?tab=payments)
 * and any other page linking into a specific tab.
 */
export function buildPayuTabUrl(tab: PayuTab): string {
  if (tab === 'connect') return '/home/payu';
  return `/home/payu?tab=${tab}`;
}
